import { EVENT_TYPES } from "@/packages/types";
import type { ActivityEntityConfig, RawActivity } from "./types";

export type ChecklistActivityType =
  | "summary"
  | "search"
  | "completion"
  | "failure"
  | "update"
  | "question";

export interface ChecklistActivityTranslations {
  item: string;
  unknownError: string;
  system: string;
  question: string;
  itemUpdated: string;
  markedIncomplete: (description: string, section: string) => string;
  completed: (description: string, section: string) => string;
  aiSummaryGenerated: (description: string) => string;
  docsFound: (count: number, description: string) => string;
  summaryFailed: (description: string, reason: string) => string;
  answered: (question: string) => string;
  clearedAnswer: (question: string) => string;
}

export function createChecklistActivityConfig(
  translations: ChecklistActivityTranslations,
): ActivityEntityConfig<ChecklistActivityType> {
  return {
    eventTypeCategories: {
      completion: "status",
      summary: "system",
      search: "content",
      failure: "system",
      update: "content",
      question: "content",
    },
    defaultCategory: "content",
    eventTypeLabels: {
      completion: (raw: RawActivity<ChecklistActivityType>) => {
        const itemDescription = String(
          raw.metadata?.itemDescription ?? translations.item,
        );
        const itemSection = raw.metadata?.itemSection;
        const section = itemSection ? ` (${String(itemSection)})` : "";
        if (raw.metadata?.isCompleted === false) {
          return translations.markedIncomplete(itemDescription, section);
        }
        return translations.completed(itemDescription, section);
      },
      summary: (raw: RawActivity<ChecklistActivityType>) => {
        const itemDescription = String(
          raw.metadata?.itemDescription ?? translations.item,
        );
        return translations.aiSummaryGenerated(itemDescription);
      },
      search: (raw: RawActivity<ChecklistActivityType>) => {
        const docsFound = Number(raw.metadata?.docsFound ?? 0);
        const itemDescription = String(
          raw.metadata?.itemDescription ?? translations.item,
        );
        return translations.docsFound(docsFound, itemDescription);
      },
      failure: (raw: RawActivity<ChecklistActivityType>) => {
        const itemDescription = String(
          raw.metadata?.itemDescription ?? translations.item,
        );
        const reason = String(
          raw.metadata?.reason ?? translations.unknownError,
        );
        return translations.summaryFailed(itemDescription, reason);
      },
      update: translations.itemUpdated,
      question: (raw: RawActivity<ChecklistActivityType>) => {
        const questionText = String(
          raw.metadata?.questionText ?? translations.question,
        );
        if (raw.eventType === EVENT_TYPES.CHECKLIST_ITEM_QUESTION_ANSWERED) {
          return translations.answered(questionText);
        }
        return translations.clearedAnswer(questionText);
      },
    },
    fieldLabels: {},
    fieldTitleLabels: {},
    metadataSkipKeys: new Set([
      "changes",
      "success",
      "data",
      "error",
      "requestId",
      "itemDescription",
      "itemSection",
      "docsFound",
      "reason",
      "errorCode",
      "isCompleted",
      "questionText",
      "questionOrder",
      "source",
    ]),
    systemActorName: translations.system,
  };
}
